import { type FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useLocale } from "../contexts/LocaleContext";
import { useWorldMapOptional } from "../contexts/WorldMapContext";
import { useToast } from "../contexts/ToastContext";
import { LocaleSwitcher } from "./LocaleSwitcher";
import { MobileMenu } from "./MobileMenu";

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, signOut } = useAuth();
  const { count, total } = useCart();
  const { t, formatMoney } = useLocale();
  const worldMap = useWorldMapOptional();
  const toast = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const cartLabel = count > 0 ? `${formatMoney(total)} (${count})` : formatMoney(0);

  const submitSearch = async (e: FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;

    if (location.pathname === "/world") {
      const ok = await worldMap?.runSearch(q);
      if (!ok) {
        toast.show(t("header.mapLoading"), "info");
      }
      return;
    }

    navigate("/world", { state: { searchQuery: q } });
  };

  const searchField = (
    <>
      <input
        type="search"
        placeholder={t("header.searchPlaceholder")}
        value={searchQuery}
        onChange={(ev) => setSearchQuery(ev.target.value)}
        aria-label={t("header.searchAria")}
      />
      <button type="submit" aria-label={t("header.searchSubmit")}>
        <i className="fas fa-search" />
      </button>
    </>
  );

  return (
    <>
    <header className={location.pathname === "/world" ? "world-header" : undefined}>
      <div className="header-container">
        <Link to="/start" className="logo">
          <img
            src="/static/img/die-welt-in-kunst_logo_V2.png"
            alt={t("footer.logoAlt")}
          />
        </Link>

        <form className="search" onSubmit={(ev) => void submitSearch(ev)}>
          {searchField}
        </form>

        <div className="header-right">
          <div className="account-links">
            <Link to="/contact">{t("topbar.contact")}</Link>

            {!isAuthenticated ? (
              <Link to="/login">{t("topbar.loginRegister")}</Link>
            ) : (
              <>
                <Link id="userDisplay" to="/profile" className="profile-link">
                  {user.picture ? (
                    <>
                      <img
                        src={user.picture}
                        alt={t("topbar.userAlt")}
                        className="profile-image"
                      />
                      {user.username || ""}
                    </>
                  ) : (
                    <>👤 {user.username || ""}</>
                  )}
                </Link>
                <a
                  id="logoutBtn"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    void signOut();
                  }}
                >
                  {t("topbar.logout")}
                </a>
              </>
            )}
          </div>

          <div className="header-tools">
            <LocaleSwitcher className="language-switch" />
            <div
              className="cart"
              onClick={() => navigate("/cart")}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-shopping-cart" /> {cartLabel}
            </div>
          </div>
        </div>
      </div>

      <nav className="main-menu">
        <Link to="/mission">{t("nav.mission")}</Link>
        <Link to="/video">{t("nav.howItWorks")}</Link>
        <Link to="/art">{t("nav.gallery")}</Link>
        <Link to="/world">{t("nav.buyFrames")}</Link>
        <div className="menu-item-with-sub">
          <a
            href="https://elke-schark-foundation.com"
            target="_blank"
            rel="noreferrer"
          >
            {t("nav.socialEngagement")}
          </a>
          <div className="submenu">
            <a
              href="https://elke-schark-foundation.com"
              target="_blank"
              rel="noreferrer"
            >
              {t("nav.elkeFoundation")}
            </a>
          </div>
        </div>
        {isAuthenticated && <Link to="/profile">{t("profile.title")}</Link>}
      </nav>

      <div className="mobile-header">
        <div className="mobile-header-bar">
          <Link to="/start" className="logo">
            <img
              src="/static/img/die-welt-in-kunst_logo_V2.png"
              alt={t("footer.logoAlt")}
            />
          </Link>

          <button
            type="button"
            className="burger"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={mobileOpen}
          >
            <i className="fas fa-bars" />
          </button>
        </div>

        <div className="mobile-header-tools">
          <div className="mobile-account-links">
            {!isAuthenticated ? (
              <Link to="/login">{t("topbar.loginRegister")}</Link>
            ) : (
              <>
                <Link to="/profile" className="mobile-profile-link">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt={t("topbar.userAlt")}
                      className="profile-image"
                    />
                  ) : (
                    <i className="fas fa-user" aria-hidden="true" />
                  )}
                  <span>{user.username || t("topbar.userAlt")}</span>
                </Link>
                <button
                  type="button"
                  className="mobile-logout-button"
                  onClick={() => void signOut()}
                >
                  {t("topbar.logout")}
                </button>
              </>
            )}
          </div>

          <div className="mobile-commerce-tools">
            <LocaleSwitcher
              className="mobile-language-switcher"
              showCurrency={false}
            />

            <div className="mobile-commerce-right">
              <LocaleSwitcher
                className="mobile-currency-switcher"
                showLanguage={false}
              />
              <button
                type="button"
                className="mobile-cart"
                onClick={() => navigate("/cart")}
                aria-label={cartLabel}
              >
                <i className="fas fa-shopping-cart" />
                <span>{cartLabel}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="mobile-search-bar">
          <form className="search" onSubmit={(ev) => void submitSearch(ev)}>
            {searchField}
          </form>
        </div>
      </div>

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
    {location.pathname === "/world" && (
      <div className="world-search-sticky">
        <div className="world-search-tools">
          <form className="search" onSubmit={(ev) => void submitSearch(ev)}>{searchField}</form>
          <div id="world-map-controls" />
        </div>
      </div>
    )}
    </>
  );
}
